import { useState, useCallback, useEffect, useRef, type ChangeEvent, type FormEvent, type KeyboardEvent } from "react";
import { validateBarcode, type BarcodeValidationResult } from "../scanner/eanValidator";
import { triggerHapticError } from "../telegram";
import type { Language } from "../i18n";
import { t } from "../i18n";

export interface ManualBarcodeInputProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (barcode: string) => void;
  language?: Language;
}

/**
 * Modal dialog for manual barcode entry with numeric keypad and EAN-8/12/13 validation.
 */
export function ManualBarcodeInput({
  isOpen,
  onClose,
  onSubmit,
  language = "ru",
}: ManualBarcodeInputProps) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset and focus when modal opens
  useEffect(() => {
    if (isOpen) {
      setValue("");
      setError(null);
      // Small timeout ensures element is mounted and rendered before focusing
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow digits, spaces, and hyphens while typing
    const sanitized = raw.replace(/[^\d\s-]/g, "");
    setValue(sanitized);
    if (error) {
      setError(null);
    }
  }, [error]);

  const handleClear = useCallback(() => {
    setValue("");
    setError(null);
    inputRef.current?.focus();
  }, []);

  const handleFormSubmit = useCallback(
    (e?: FormEvent) => {
      if (e) e.preventDefault();
      const validation: BarcodeValidationResult = validateBarcode(value, language);
      if (!validation.isValid) {
        triggerHapticError();
        setError(validation.error || t(language, "val_invalid_format"));
        return;
      }
      setError(null);
      onSubmit(validation.normalized);
    },
    [value, language, onSubmit]
  );

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleFormSubmit();
      }
    },
    [handleFormSubmit]
  );

  if (!isOpen) return null;

  const cleaned = value.replace(/[\s-]/g, "");
  const digitCount = cleaned.length;
  const liveValidation = digitCount > 0 ? validateBarcode(value, language) : null;

  return (
    <div
      className="manual-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="manual-barcode-title"
      data-testid="manual-barcode-modal"
    >
      <div className="manual-modal__backdrop" onClick={onClose} />
      <div className="manual-modal__card">
        <div className="manual-modal__header">
          <div className="manual-modal__title-row">
            <span className="manual-modal__icon">🔢</span>
            <h2 id="manual-barcode-title" className="manual-modal__title">
              {t(language, "manual_title")}
            </h2>
          </div>
          <button
            type="button"
            className="manual-modal__close-btn"
            onClick={onClose}
            aria-label={t(language, "manual_close_aria")}
            data-testid="manual-barcode-close"
          >
            ✕
          </button>
        </div>

        <p className="manual-modal__hint">
          {t(language, "manual_hint")}
        </p>

        <form onSubmit={handleFormSubmit} className="manual-modal__form">
          <div className="manual-input__wrapper">
            <input
              ref={inputRef}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              className={`manual-input__field ${error ? "manual-input__field--error" : ""}`}
              placeholder={t(language, "manual_placeholder")}
              value={value}
              onChange={handleChange}
              onKeyDown={handleInputKeyDown}
              data-testid="manual-barcode-input"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "manual-barcode-error-text" : undefined}
            />
            {value.length > 0 && (
              <button
                type="button"
                className="manual-input__clear"
                onClick={handleClear}
                aria-label={t(language, "manual_clear_aria")}
                data-testid="manual-barcode-clear"
              >
                ✕
              </button>
            )}
          </div>

          <div className="manual-modal__meta">
            <span className="manual-modal__counter">
              {t(language, "manual_counter")}<strong>{digitCount}</strong> / 13
            </span>
            {liveValidation?.isValid && (
              <span className="manual-modal__badge manual-modal__badge--valid">
                ✓ {liveValidation.barcodeType}
              </span>
            )}
          </div>

          {error && (
            <div
              id="manual-barcode-error-text"
              className="manual-modal__error"
              role="alert"
              data-testid="manual-barcode-error"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="manual-modal__actions">
            <button
              type="button"
              className="manual-modal__btn manual-modal__btn--cancel"
              onClick={onClose}
              data-testid="manual-barcode-cancel"
            >
              {t(language, "manual_cancel_btn")}
            </button>
            <button
              type="submit"
              className="manual-modal__btn manual-modal__btn--submit"
              disabled={digitCount === 0}
              data-testid="manual-barcode-submit"
            >
              {t(language, "manual_submit_btn")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
